import React, { useState, useMemo } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { Download, RefreshCw } from 'lucide-react'

import { cn } from '@/lib/utils'
import { currencyFormatter } from '../../library/general'

import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert } from '@/components/ui/alert'
import { Spinner } from '@/components/ui/spinner'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { DataTable } from '@/components/ui/data-table'

import {
  PRODUCT_NAME_COLUMN,
  DISCOUNT_COLUMN,
  QUANTITY_COLUMN,
  UNIT_PRICE_COLUMN,
  TOTAL_COLUMN,
  PRODUCT_NAME_COLUMN_FRIENDLY,
  DISCOUNT_COLUMN_FRIENDLY,
  QUANTITY_COLUMN_FRIENDLY,
  UNIT_PRICE_COLUMN_FRIENDLY,
  TOTAL_COLUMN_FRIENDLY,
  requestOrder,
  requestOrderFiles,
  requestOrderCSV,
} from '../../stores/order'

import DownloadableFiles from '../../components/DownloadableFiles'

import './index.css'

function statusVariant(status) {
  if (!status) return 'secondary'
  const s = status.toLowerCase()
  if (s.includes('complete')) return 'success'
  if (s.includes('progress') || s.includes('active') || s.includes('process')) return 'default'
  if (s.includes('cancel')) return 'destructive'
  if (s.includes('hold') || s.includes('pending')) return 'warning'
  return 'secondary'
}

function deliveryStatusDesc(status) {
  if (status === 'Confirmed') return 'All assets delivered. Times in GMT.'
  if (status === 'Initiated') return 'Dispatched. Confirmation may take minutes to hours.'
  return 'Transcoding, broadcast correction, or captioning prep in progress.'
}

function deliveryStatusVariant(status) {
  if (status === 'Confirmed') return 'success'
  if (status === 'Initiated') return 'default'
  return 'secondary'
}

// A single labelled field in the sidebar
function PanelField({ label, value }) {
  if (!value && value !== 0) return null
  return (
    <div className="border-b px-3 py-1.5">
      <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="break-words text-sm font-medium leading-snug">{value}</div>
    </div>
  )
}

// One row in the timeline
function TimelineItem({ label, value, done, isLast }) {
  return (
    <div className="flex gap-3">
      <div className="flex shrink-0 flex-col items-center">
        <span
          className={cn(
            'mt-[3px] h-2.5 w-2.5 shrink-0 rounded-full',
            done ? 'bg-success ring-2 ring-success/20' : 'border-2 border-primary bg-white ring-2 ring-primary/15'
          )}
        />
        {!isLast && <span className="mt-1 min-h-[18px] w-px flex-1 bg-border" />}
      </div>
      <div className={cn(isLast ? 'pb-0' : 'pb-3')}>
        <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className={cn('text-[13px] font-semibold', done ? 'text-success' : 'text-foreground')}>
          {value || '—'}
        </div>
      </div>
    </div>
  )
}

function ViewSalesOrder({ id, order, requestOrder, requestOrderFiles, requestOrderCSV }) {
  const [tab, setTab] = useState('line')

  React.useEffect(() => {
    if (order && !order.fetched) requestOrder(id)
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Extract flags before early return so hooks always run in the same order
  const orderHasAberFastDelivery = order?.orderHasAberFastDelivery || false
  const hasAccessToOrderTotals   = order?.hasAccessToOrderTotals   || false
  const orderHasDiscounts        = order?.orderHasDiscounts        || false

  const fmtDatetime = val =>
    val
      ? `${new Date(val).toLocaleDateString('en-US', { timeZone: 'UTC' })} ${new Date(val).toLocaleTimeString('en-US', { timeZone: 'UTC' })}`
      : ''

  const lineItemColumns = useMemo(() => {
    const cols = [
      {
        id: '#', header: '#', accessorKey: '#', size: 38,
        enableSorting: false, enableResizing: false,
        meta: { reorderable: false, align: 'right', cellClassName: 'text-muted-foreground' },
      },
      {
        id: 'name', header: 'Description', accessorKey: PRODUCT_NAME_COLUMN,
        size: orderHasAberFastDelivery ? 220 : 600, enableSorting: false,
        cell: ({ getValue }) => { const v = getValue() || ''; return <span title={v}>{v}</span> },
        meta: { headerTitle: PRODUCT_NAME_COLUMN_FRIENDLY },
      },
    ]
    if (orderHasAberFastDelivery) {
      cols.push(
        {
          id: 'station', header: 'Station', size: 90, enableSorting: false,
          meta: { headerTitle: 'Airing Station(s)', cellClassName: 'whitespace-normal' },
          cell: ({ row }) => row.original.airingStations?.length ? (
            <ul className="m-0 list-disc pl-4 text-[12px]">
              {row.original.airingStations.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          ) : null,
        },
        {
          id: 'location', header: 'Location', size: 90, enableSorting: false,
          meta: { headerTitle: 'Delivery Location(s)', cellClassName: 'whitespace-normal' },
          cell: ({ row }) => row.original.deliveryLocations?.length ? (
            <ul className="m-0 list-disc pl-4 text-[12px]">
              {row.original.deliveryLocations.map((l, i) => <li key={i}>{l}</li>)}
            </ul>
          ) : null,
        },
        {
          id: 'status', header: 'Status', size: 120, enableSorting: false,
          meta: { headerTitle: 'Delivery Status' },
          cell: ({ row }) => row.original.deliveryStatus ? (
            <Badge variant={deliveryStatusVariant(row.original.deliveryStatus)} title={deliveryStatusDesc(row.original.deliveryStatus)}>
              {row.original.deliveryStatus}
            </Badge>
          ) : null,
        },
        {
          id: 'confirmed', header: 'GMT Confirmed', size: 150, enableSorting: false,
          meta: { headerTitle: 'Delivery Confirmed (GMT)' },
          cell: ({ row }) => <span className="text-[12px]">{fmtDatetime(row.original.deliveryConfirmedTime)}</span>,
        },
      )
    }
    if (hasAccessToOrderTotals) {
      cols.push(
        {
          id: 'qty', header: 'Qty', accessorKey: QUANTITY_COLUMN, size: 60, enableSorting: false,
          meta: { headerTitle: QUANTITY_COLUMN_FRIENDLY, align: 'right' },
        },
        {
          id: 'unitPrice', header: 'Unit $', size: 100, enableSorting: false,
          meta: { headerTitle: UNIT_PRICE_COLUMN_FRIENDLY, align: 'right' },
          cell: ({ row }) => currencyFormatter.format(row.original[UNIT_PRICE_COLUMN]),
        },
      )
      if (orderHasDiscounts) {
        cols.push({
          id: 'discount', header: 'Disc.', size: 70, enableSorting: false,
          meta: { headerTitle: DISCOUNT_COLUMN_FRIENDLY, align: 'right' },
          cell: ({ row }) => `${row.original[DISCOUNT_COLUMN]}%`,
        })
      }
      cols.push({
        id: 'total', header: 'Total', size: 100, enableSorting: false,
        meta: { headerTitle: TOTAL_COLUMN_FRIENDLY, align: 'right' },
        cell: ({ row }) => currencyFormatter.format(row.original[TOTAL_COLUMN]),
      })
    }
    return cols
  }, [orderHasAberFastDelivery, hasAccessToOrderTotals, orderHasDiscounts]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = async newTab => {
    setTab(newTab)
    if (newTab === 'files' && order && !order.fetchedOrderFiles) {
      await requestOrderFiles(id)
    }
  }

  if (!order || (!order.fetched && !order.hasError)) {
    return (
      <div className="flex items-center gap-2 p-8">
        <Spinner size={20} />
        <span className="text-sm text-muted-foreground">Loading order…</span>
      </div>
    )
  }

  // Fetch failed before the order ever loaded — show the error instead of an
  // endless spinner (the inline Alert further down only renders once fetched).
  if (!order.fetched && order.hasError) {
    return (
      <div className="max-w-xl space-y-3 p-2">
        <Alert variant="error">
          {order.error || 'Something went wrong loading this order. Please try again.'}
        </Alert>
        <Button variant="outline" size="sm" onClick={() => requestOrder(id)} disabled={order.loading}>
          {order.loading ? <Spinner size={16} /> : <RefreshCw className="h-4 w-4" />} Retry
        </Button>
      </div>
    )
  }

  const {
    hasError,
    error,
    loading,
    files,
    orderLines,
    orderName,
    airDate,
    dueDate,
    completionDate,
    episodeNumber,
    episodeTitle,
    sourceFileName,
    sourceReceivedDate,
    orderStatus,
    totalPrice,
    createdDate,
    accountName,
    programVersion,
    externalNotes,
  } = order

  const fmtDate = (val, utc = false) =>
    val ? new Date(val).toLocaleDateString('en-US', utc ? { timeZone: 'UTC' } : undefined) : null

  const now = new Date()
  const isPast = val => val && (new Date(val) <= now || statusVariant(orderStatus) === 'success')

  // Timeline milestones in chronological order
  const milestones = [
    sourceReceivedDate && { label: 'Source Received', value: fmtDate(sourceReceivedDate), done: isPast(sourceReceivedDate) },
    createdDate && { label: 'Order Created', value: fmtDate(createdDate), done: isPast(createdDate) },
    completionDate
      ? { label: 'Completed', value: fmtDate(completionDate), done: isPast(completionDate) }
      : dueDate && { label: 'Due Date', value: fmtDate(dueDate), done: isPast(dueDate) },
    airDate && { label: 'Air Date', value: fmtDate(airDate, true), done: isPast(airDate) },
  ].filter(Boolean)

  return (
    <div className="space-y-3">
      {hasError && <Alert variant="error">{error}</Alert>}

      {/* ── Title row ── */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <h1 className="text-lg font-bold">{orderName || 'Order'}</h1>
          {orderStatus && <Badge variant={statusVariant(orderStatus)}>{orderStatus}</Badge>}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => requestOrderCSV(id)}
          title="Export CSV of order with line items"
        >
          <Download className="h-4 w-4" /> Export CSV
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          onClick={() => requestOrder(id)}
          disabled={loading}
          title="Refresh"
        >
          {loading ? <Spinner size={16} /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
        {/* ── Left panel ── */}
        <Card className="overflow-hidden lg:w-72 lg:shrink-0">
          <PanelField label="Program" value={programVersion} />
          <PanelField label="Account" value={accountName} />
          {episodeTitle && <PanelField label="Episode" value={episodeTitle} />}
          {episodeNumber && <PanelField label="Episode #" value={episodeNumber} />}
          {sourceFileName && <PanelField label="Source" value={sourceFileName} />}
          {hasAccessToOrderTotals && !!totalPrice && (
            <PanelField label="Total" value={currencyFormatter.format(totalPrice)} />
          )}

          {milestones.length > 0 && (
            <>
              <div className="border-y bg-muted/40 px-3 py-1.5">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-muted-foreground">
                  Timeline
                </span>
              </div>
              <div className="px-3 pb-1 pt-2">
                {milestones.map((m, i) => (
                  <TimelineItem
                    key={m.label}
                    label={m.label}
                    value={m.value}
                    done={m.done}
                    isLast={i === milestones.length - 1}
                  />
                ))}
              </div>
            </>
          )}

          {externalNotes && (
            <>
              <Separator />
              <div className="px-3 py-2">
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Notes
                </div>
                <p className="text-[13px] leading-relaxed text-muted-foreground">{externalNotes}</p>
              </div>
            </>
          )}
        </Card>

        {/* ── Right: tabs ── */}
        <Card className="min-w-0 flex-1 overflow-hidden">
          <Tabs value={tab} onValueChange={handleTabChange}>
            <TabsList className="border-b px-2">
              <TabsTrigger value="line">Line Items</TabsTrigger>
              <TabsTrigger value="files">Files</TabsTrigger>
            </TabsList>

            <TabsContent value="line">
              <DataTable
                tableId={`order-lines${orderHasAberFastDelivery ? '-af' : ''}`}
                columns={lineItemColumns}
                data={orderLines || []}
                fitMode={orderHasAberFastDelivery ? 'fixed' : 'fill'}
                minWidth={orderHasAberFastDelivery ? 880 : 0}
                getRowId={(row, i) => String(i)}
                emptyMessage="No line items"
              />
            </TabsContent>

            <TabsContent value="files" className="p-4">
              {!order.fetchedOrderFiles ? (
                <div className="flex items-center gap-2">
                  <Spinner size={18} />
                  <span className="text-sm text-muted-foreground">Loading files…</span>
                </div>
              ) : files && files.length > 0 ? (
                <DownloadableFiles files={files} />
              ) : (
                <p className="text-sm text-muted-foreground">
                  No files available yet. As assets are processed they will appear here.
                </p>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  )
}

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators({ requestOrder, requestOrderFiles, requestOrderCSV }, dispatch)
})

const mapStateToProps = (state, route) => {
  const sfidRegex = /\/orders\/(.*)/ig
  const sfid = sfidRegex.exec(route.location.pathname)[1]
  const { order } = state.stores.order
  const orderToPass = order.hasOwnProperty(sfid) ? order[sfid] : {}

  return {
    ...route.match.params,
    order: orderToPass,
    id: sfid,
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ViewSalesOrder))
